<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";

export default {
  locales: {
    pt: {},
    es: {},
  },
  components: {
    Layout,
  },
  data() {
    return {
      card: null,
      cardError: null,
      physical: null,
      physicalError: null,
      pageLoading: true,
      checkoutModal: false,
      checkoutSubmitting: false,
      pollTimer: null,
      addressForm: {
        zipCode: "",
        address: "",
        addressNumber: "",
        addressComplement: "",
        district: "",
        city: "",
        state: "",
      },
    };
  },
  methods: {
    fetchCard() {
      this.cardError = null;
      this.card = null;
      return api
        .get("plans/carteirinha")
        .then((response) => {
          const payload = response.data;
          if (payload.status === "success" && payload.card) {
            this.card = payload.card;
          } else {
            const message =
              payload.message ||
              "Não foi possível carregar a carteirinha.";
            this.cardError = message;
            this.$toast.error(message);
          }
        })
        .catch(() => {
          const message = "Erro de conexão ao carregar a carteirinha.";
          this.cardError = message;
          this.$toast.error(message);
        });
    },
    fetchPhysical() {
      return api.get("plans/carteirinha/physical").then((response) => {
        const payload = response.data;
        if (payload.status === "success") {
          this.physical = payload;
          this.physicalError = null;
          this.managePhysicalPoll(payload);
        } else {
          this.physicalError =
            payload.message ||
            "Não foi possível carregar dados da carteirinha física.";
          this.physical = payload;
          this.$toast.error(this.physicalError);
          this.stopPhysicalPoll();
        }
      }).catch(() => {
        const message =
          "Erro de conexão ao carregar a carteirinha física.";
        this.physicalError = message;
        this.$toast.error(message);
        this.stopPhysicalPoll();
      });
    },
    managePhysicalPoll(payload) {
      const pay = payload.payment;
      if (pay && pay.status === "pending") {
        this.startPhysicalPoll();
      } else {
        this.stopPhysicalPoll();
      }
    },
    startPhysicalPoll() {
      this.stopPhysicalPoll();
      this.pollTimer = setInterval(() => {
        api.get("plans/carteirinha/physical").then((response) => {
          const payload = response.data;
          if (payload.status === "success") {
            this.physical = payload;
            this.managePhysicalPoll(payload);
          }
        }).catch(() => {});
      }, 22000);
    },
    stopPhysicalPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    openCheckoutModal() {
      const suggested = this.physical && this.physical.deliveryAddressSuggested;
      if (suggested) {
        this.addressForm = {
          zipCode: suggested.zipcode || "",
          address: suggested.address || "",
          addressNumber: suggested.number || "",
          addressComplement: suggested.complement || "",
          district: suggested.district || "",
          city: suggested.city || "",
          state: (suggested.state || "").substring(0, 2),
        };
      } else {
        this.addressForm = {
          zipCode: "",
          address: "",
          addressNumber: "",
          addressComplement: "",
          district: "",
          city: "",
          state: "",
        };
      }
      this.checkoutModal = true;
    },
    submitPhysicalCheckout() {
      this.checkoutSubmitting = true;
      api
        .post("plans/carteirinha/physical", this.addressForm)
        .then((response) => {
          const payload = response.data;
          if (payload.status === "success") {
            this.$toast.success(
              payload.message || "Pedido gerado. Conclua o pagamento via Pix."
            );
            this.checkoutModal = false;
            this.physical = payload;
            this.managePhysicalPoll(payload);
          } else {
            this.$toast.error(
              payload.message || "Não foi possível concluir a solicitação."
            );
          }
        })
        .catch(() => {
          this.$toast.error("Erro de conexão ao enviar o endereço.");
        })
        .finally(() => {
          this.checkoutSubmitting = false;
        });
    },
    loadPage() {
      this.pageLoading = true;
      Promise.all([this.fetchCard()]).finally(() => {
        this.pageLoading = false;
      });
    },
  },
  mounted() {
    this.loadPage();
  },
  beforeDestroy() {
    this.stopPhysicalPoll();
  },
};
</script>

<template>
  <Layout>
    <div class="page-title-box d-md-flex justify-content-between mb-3">
      <div class="flex-grow-1">
        <h3>Carteirinha</h3>
      </div>
    </div>

    <div
      v-if="physical && physical.hasEligiblePurchase && !physical.requested"
      class="alert alert-info d-flex align-items-center justify-content-between flex-wrap mb-3"
      role="alert"
    >
      <div class="mr-3 mb-2 mb-md-0">
        Adquirir a carteirinha física
        <span v-if="physical.price != null" class="font-weight-bold">
          — {{ physical.price | currency }}
        </span>
      </div>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="pageLoading"
        @click="openCheckoutModal"
      >
        Comprar carteirinha física
      </button>
    </div>

    <div v-if="physicalError" class="alert alert-warning mb-3" role="alert">
      {{ physicalError }}
    </div>

    <div v-if="pageLoading" class="text-center py-4">
      <b-spinner variant="default" role="status"></b-spinner>
    </div>

    <template v-else>
      <div v-if="cardError" class="alert alert-danger mb-3">
        {{ cardError }}
      </div>

      <div class="d-flex justify-content-center mb-4">
        <img
          v-if="card"
          class="rounded-lg w-100"
          style="max-width: 500px;"
          :src="'data:image/png;base64,' + card"
          alt="Carteirinha digital"
        />
      </div>

      <div v-if="physical && physical.requested" class="card border shadow-none mb-4">
        <div class="card-body">
          <h5 class="card-title">Carteirinha física</h5>

          <div
            v-if="physical.payment && physical.payment.status === 'pending' && physical.pix && physical.pix.text"
            class="text-center"
          >
            <p class="text-muted">
              Pedido gerado. Efetue o pagamento via Pix para concluir a compra.
            </p>
            <p class="mb-1">
              <strong>{{ physical.payment.value | currency }}</strong>
            </p>
            <div v-if="physical.pix.image" class="my-4 d-flex justify-content-center">
              <div class="border rounded p-3">
                <img
                  :src="'data:image/png;base64,' + physical.pix.image"
                  alt="QR Pix"
                  style="width: 180px;"
                />
              </div>
            </div>
            <p class="text-left mb-3">
              Abra o app do seu banco, Pix copia e cola, cole o código abaixo ou leia o QR.
            </p>
            <div class="border rounded p-3 mb-3 text-left text-break font-size-12">
              {{ physical.pix.text }}
            </div>
            <button
              type="button"
              class="btn btn-lg btn-primary btn-block"
              v-clipboard:copy="physical.pix.text"
              @click="
                $toast.success(
                  'Código Pix copiado. Cole no aplicativo do banco para pagar.'
                )
              "
            >
              Copiar Pix copia e cola
            </button>
          </div>

          <div
            v-else-if="physical.payment && physical.payment.status === 'pending'"
            class="text-center text-muted"
          >
            <p>Gerando ou carregando o Pix...</p>
            <b-spinner small variant="default"></b-spinner>
          </div>

          <div
            v-else-if="physical.payment && physical.payment.status === 'approved'"
            class="alert alert-success mb-3"
            role="alert"
          >
            Pagamento aprovado. Acompanhe o status da entrega abaixo quando houver novidades.
          </div>

          <div v-if="physical.orderStatuses && physical.orderStatuses.length">
            <h6 class="mt-4 mb-3">Status da entrega</h6>
            <ul class="list-unstyled mb-0">
              <li
                v-for="(row, idx) in physical.orderStatuses"
                :key="'st-' + idx"
                class="mb-3 border-bottom pb-2"
              >
                <div class="text-muted small">{{ row.date }}</div>
                <div>{{ row.status }}</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>

    <b-modal
      v-model="checkoutModal"
      title="Confirmar endereço de entrega"
      ok-title="Continuar"
      cancel-title="Cancelar"
      :ok-disabled="checkoutSubmitting"
      :cancel-disabled="checkoutSubmitting"
      @ok.prevent="submitPhysicalCheckout"
    >
      <p class="text-muted small">
        Confira os dados para envio da carteirinha física. O pagamento será exclusivo via Pix.
      </p>
      <div class="form-group">
        <label>CEP</label>
        <input v-model="addressForm.zipCode" type="text" class="form-control" maxlength="9" />
      </div>
      <div class="form-group">
        <label>Endereço</label>
        <input v-model="addressForm.address" type="text" class="form-control" />
      </div>
      <div class="form-row">
        <div class="form-group col-md-6">
          <label>Número</label>
          <input v-model="addressForm.addressNumber" type="text" class="form-control" />
        </div>
        <div class="form-group col-md-6">
          <label>Complemento</label>
          <input v-model="addressForm.addressComplement" type="text" class="form-control" />
        </div>
      </div>
      <div class="form-group">
        <label>Bairro</label>
        <input v-model="addressForm.district" type="text" class="form-control" />
      </div>
      <div class="form-row">
        <div class="form-group col-md-8">
          <label>Cidade</label>
          <input v-model="addressForm.city" type="text" class="form-control" />
        </div>
        <div class="form-group col-md-4">
          <label>UF</label>
          <input v-model="addressForm.state" type="text" class="form-control" maxlength="2" />
        </div>
      </div>
    </b-modal>
  </Layout>
</template>
